import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useState, useEffect, useMemo } from 'react';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import {
  useGetAllEvents,
  useGetPublicEvents,
  getEventCoverImage,
  getEventLocation,
  getTicketLabel,
  getPublicEventPriceLabel,
  type EventItem,
  type PublicEventItem,
} from '../api/events';
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiArrowRight,
  FiStar,
  FiUsers,
  FiAlertCircle,
  FiList,
  FiEdit2,
  FiShoppingBag,
  FiFileText,
  FiGlobe,
  FiPlus,
  FiExternalLink,
} from 'react-icons/fi';

export const Route = createFileRoute('/events/')({
  component: EventsPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

// ─── Public Event Card ────────────────────────────────────────────────────────

function PublicEventCard({ event }: { event: PublicEventItem }) {
  const heroImg = event.featured_image?.file_url ?? event.featured_image?.image;
  const locationStr = [event.city, event.country].filter(Boolean).join(', ');
  const price = getPublicEventPriceLabel(event);

  return (
    <Link to="/events/$eventId" params={{ eventId: String(event.id) }}>
      <div className="group relative bg-zinc-900 rounded-2xl overflow-hidden border border-white/5 hover:border-white/15 transition-all duration-200 hover:scale-[1.02] cursor-pointer">
        <div className="relative w-full aspect-square bg-zinc-800 overflow-hidden">
          {heroImg ? (
            <img
              src={heroImg}
              alt={event.event_title ?? ''}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FiCalendar size={32} className="text-zinc-700" />
            </div>
          )}
          <div className="absolute inset-0 bg-linear-to-b from-black/40 to-transparent" />
        </div>
        <div className="p-3 space-y-1.5">
          <p className="text-white text-[13px] font-bold leading-tight line-clamp-2">
            {event.event_title ?? 'Untitled Event'}
          </p>
          {locationStr && (
            <p className="text-zinc-400 text-[12px] flex items-center gap-1 truncate">
              <FiMapPin size={11} className="shrink-0" />
              {locationStr}
            </p>
          )}
          {event.start && (
            <p className="text-zinc-500 text-[11px] flex items-center gap-1">
              <FiCalendar size={11} className="shrink-0" />
              {formatDate(event.start)}
            </p>
          )}
          {price && (
            <span
              className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-md ${
                price.variant === 'free' ? 'bg-yellow-400 text-black' : 'bg-white text-black'
              }`}
            >
              {price.text}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─── Public Events skeleton ───────────────────────────────────────────────────

function PublicEventsSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-zinc-900 rounded-2xl overflow-hidden animate-pulse">
          <div className="aspect-square bg-zinc-800" />
          <div className="p-3 space-y-2">
            <div className="h-3 bg-zinc-700 rounded w-4/5" />
            <div className="h-3 bg-zinc-800 rounded w-3/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Public Events preview (authenticated users only) ────────────────────────

function PublicEventsPreview({
  events,
  isLoading,
}: {
  events: PublicEventItem[];
  isLoading: boolean;
}) {
  if (!isLoading && events.length === 0) return null;
  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 p-4 rounded-2xl border border-white/8 bg-white/3 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-zinc-400 bg-white/8">
          <FiGlobe size={17} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-[15px] font-bold leading-tight">Public Events</p>
          <p className="text-zinc-500 text-[12px] mt-0.5">Upcoming events around you</p>
        </div>
      </div>
      {isLoading ? (
        <PublicEventsSkeleton count={4} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {events.map((event) => (
            <PublicEventCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Event Card ───────────────────────────────────────────────────────────────

function EventCard({ event }: { event: EventItem }) {
  const cover = getEventCoverImage(event);
  const location = getEventLocation(event);
  const ticket = getTicketLabel(event);
  const isMulti = event.event_structure_type === 'multi_event';
  const isDraft = event.is_draft_or_published === 'draft';
  const isExternal =
    event.event_ticketing_type === 'external' || event.event_structure_type === 'external';

  return (
    <Link to="/events/$eventId" params={{ eventId: String(event.id) }}>
      <div className="group relative bg-zinc-900 rounded-2xl overflow-hidden border border-white/5 hover:border-white/15 transition-all duration-200 hover:scale-[1.02] cursor-pointer">
        {/* Cover image */}
        <div className="relative w-full aspect-square bg-zinc-800 overflow-hidden">
          {cover ? (
            <img
              src={cover}
              alt={event.event_title ?? ''}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FiCalendar size={32} className="text-zinc-700" />
            </div>
          )}
          {/* gradient */}
          <div className="absolute inset-0 bg-linear-to-b from-black/40 to-transparent" />
          {/* Cancelled badge — top-left */}
          {event.is_cancelled && (
            <div className="absolute top-2 left-2 flex items-center gap-1 bg-red-500/90 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg">
              <FiAlertCircle size={11} />
              Cancelled
            </div>
          )}
          {/* Draft badge — top-right */}
          {isDraft && !event.is_cancelled && (
            <div className="absolute top-2 right-2 flex items-center gap-1 bg-amber-500/30 backdrop-blur-sm text-amber-300 text-[11px] font-bold px-2.5 py-1 rounded-lg border border-amber-500/20">
              Draft
            </div>
          )}
          {/* Multi-event badge — top-right */}
          {isMulti && !event.is_cancelled && !isDraft && !isExternal && (
            <div className="absolute top-2 right-2 bg-blue-500/30 backdrop-blur-sm text-white/80 text-[11px] font-bold px-2.5 py-1 rounded-lg">
              Multi-Event
            </div>
          )}
        </div>
        {/* Info */}
        <div className="p-3 space-y-1.5">
          <p className="text-white text-[13px] font-bold leading-tight line-clamp-2">
            {event.event_title ?? 'Untitled Event'}
          </p>
          {location && (
            <p className="text-zinc-400 text-[12px] flex items-center gap-1 truncate">
              <FiMapPin size={11} className="shrink-0" />
              {location}
            </p>
          )}
          {event.start && (
            <p className="text-zinc-500 text-[11px] flex items-center gap-1">
              <FiCalendar size={11} className="shrink-0" />
              {formatDate(event.start)}
            </p>
          )}
          {ticket && (
            <span
              className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-md ${
                ticket.variant === 'free' ? 'bg-yellow-400 text-black' : 'bg-white text-black'
              }`}
            >
              {ticket.text}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  subtitle: string;
  icon: ReactNode;
  events: EventItem[];
  isWarning?: boolean;
  filterName: string;
  hideViewAll?: boolean;
}

function EventSection({
  title,
  subtitle,
  icon,
  events,
  isWarning = false,
  filterName,
  hideViewAll = false,
}: SectionProps) {
  const navigate = useNavigate();
  if (events.length === 0) return null;

  const shown = events.slice(0, 4);
  const remaining = events.length - 4;
  const handleViewAll = () =>
    navigate({ to: '/events/category/$filterName', params: { filterName } });

  return (
    <div className="mb-10">
      {/* Header */}
      <div
        className={`flex items-center gap-3 p-4 rounded-2xl border mb-4 ${
          isWarning ? 'bg-orange-500/8 border-orange-500/25' : 'bg-white/3 border-white/8'
        }`}
      >
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
            isWarning ? 'text-orange-400 bg-orange-500/20' : 'text-zinc-400 bg-white/8'
          }`}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-[15px] font-bold leading-tight">{title}</p>
          <p className="text-zinc-500 text-[12px] mt-0.5">{subtitle}</p>
        </div>
        {!hideViewAll && (
          <button
            onClick={handleViewAll}
            className={`flex items-center gap-1 text-[12px] font-semibold shrink-0 transition-colors ${
              isWarning ? 'text-orange-400 hover:text-orange-300' : 'text-zinc-400 hover:text-white'
            }`}
          >
            View all <FiArrowRight size={13} />
          </button>
        )}
      </div>
      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {shown.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
      {/* View more */}
      {remaining > 0 && (
        <button
          onClick={handleViewAll}
          className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/8 hover:border-white/15 bg-white/3 hover:bg-white/5 text-zinc-400 hover:text-white text-[13px] font-medium transition-all"
        >
          <FiList size={15} />
          View All ({events.length}+ events)
        </button>
      )}
    </div>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function EventsSkeleton() {
  return (
    <div className="animate-pulse space-y-10">
      {[1, 2, 3].map((i) => (
        <div key={i}>
          <div className="h-16 bg-zinc-900 rounded-2xl mb-4" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="bg-zinc-900 rounded-2xl overflow-hidden">
                <div className="aspect-square bg-zinc-800" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-zinc-700 rounded w-4/5" />
                  <div className="h-3 bg-zinc-800 rounded w-3/5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function EventsPage() {
  const { isAuthenticated } = useAuth();

  // Public events — paginated for guests, first-page preview for auth users
  const [publicPage, setPublicPage] = useState(1);
  const [allPublicEvents, setAllPublicEvents] = useState<PublicEventItem[]>([]);

  const {
    data: publicEventsData,
    isLoading: publicEventsLoading,
    isFetching: publicEventsFetching,
  } = useGetPublicEvents(publicPage, isAuthenticated ? 4 : 12, {
    refetchOnMount: 'always',
    staleTime: 0,
  });

  // Accumulate pages for guest infinite-scroll style load more
  useEffect(() => {
    if (isAuthenticated) return;
    if (!publicEventsData?.events?.length) return;

    // Only accumulate for pages > 1. Page 1 is handled by useMemo.
    if (publicPage > 1) {
      setAllPublicEvents((prev) => {
        // Prevent duplicates if the effect runs twice
        const exists = prev.some((e) => e.id === publicEventsData.events[0].id);
        if (exists) return prev;
        return [...prev, ...publicEventsData.events];
      });
    }
  }, [publicEventsData, publicPage, isAuthenticated]);

  // Reset when auth state changes
  useEffect(() => {
    setPublicPage(1);
    setAllPublicEvents([]);
  }, [isAuthenticated]);

  const displayedPublicEvents = useMemo(() => {
    if (isAuthenticated) return [];
    if (publicPage === 1) return publicEventsData?.events ?? [];
    return allPublicEvents;
  }, [allPublicEvents, publicEventsData, publicPage, isAuthenticated]);

  const { data, isLoading, isError, refetch } = useGetAllEvents({ enabled: isAuthenticated });

  const isEmpty =
    !isLoading &&
    !!data &&
    [
      data.recommended_events,
      data.recent,
      data.my_created_events,
      data.collaborators,
      data.ticket_bought,
      data.cancelled,
      data.event_draft,
      data.draft_single_events,
      data.draft_multi_events,
    ].every((arr) => arr.length === 0);

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />
      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="flex items-center justify-between gap-4 mt-2 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Events</h1>
            <p className="text-zinc-500 text-sm mt-1">
              {isAuthenticated ? 'Manage and discover events' : 'Discover upcoming events'}
            </p>
          </div>
          {isAuthenticated && (
            <Link
              to="/events/create"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-[14px] font-semibold px-5 py-2.5 rounded-full transition-all active:scale-95 self-start sm:self-auto"
            >
              <FiPlus size={16} />
              Create Event
            </Link>
          )}
        </div>

        {/* ── GUEST MODE: full paginated public events ── */}
        {!isAuthenticated && (
          <>
            {publicEventsLoading && displayedPublicEvents.length === 0 ? (
              <PublicEventsSkeleton count={12} />
            ) : displayedPublicEvents.length === 0 && !publicEventsLoading ? (
              <div className="flex flex-col items-center gap-4 py-20 text-center">
                <FiCalendar size={40} className="text-zinc-600" />
                <p className="text-zinc-400 text-sm">No public events available right now.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                  {displayedPublicEvents.map((event) => (
                    <PublicEventCard key={event.id} event={event} />
                  ))}
                </div>
                {publicEventsFetching && displayedPublicEvents.length > 0 && (
                  <PublicEventsSkeleton count={4} />
                )}
                {publicEventsData?.next && !publicEventsFetching && (
                  <div className="flex justify-center mt-4 mb-8">
                    <button
                      className="px-10 py-3 rounded-full border border-white/20 text-white text-sm font-medium hover:bg-white/5 transition-all active:scale-95"
                      onClick={() => setPublicPage((prev) => prev + 1)}
                    >
                      Load More
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── AUTHENTICATED MODE: public preview + personal events ── */}
        {isAuthenticated && (
          <>
            <PublicEventsPreview
              events={(publicEventsData?.events ?? []).slice(0, 4)}
              isLoading={publicEventsLoading}
            />

            {isLoading && <EventsSkeleton />}

            {isError && (
              <div className="flex flex-col items-center gap-4 py-20 text-center">
                <FiAlertCircle size={40} className="text-zinc-600" />
                <p className="text-zinc-400 text-sm">Failed to load events. Please try again.</p>
                <Button onClick={() => refetch()} variant="secondary" className="rounded-full px-6">
                  Try Again
                </Button>
              </div>
            )}

            {isEmpty && !isLoading && (
              <div className="flex flex-col items-center gap-6 py-20 text-center">
                <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center">
                  <FiCalendar size={36} className="text-zinc-600" />
                </div>
                <div>
                  <h2 className="text-white text-lg font-semibold">No Events Yet</h2>
                  <p className="text-zinc-500 text-sm mt-1 max-w-xs mx-auto">
                    You don't have any events yet.
                  </p>
                </div>
              </div>
            )}

            {data && !isLoading && !isEmpty && (
              <>
                <EventSection
                  title="Recommended For You"
                  subtitle="Based on your preferences"
                  icon={<FiStar size={17} />}
                  events={data.recommended_events}
                  filterName="all_events"
                  hideViewAll
                />
                <EventSection
                  title="Recent Events"
                  subtitle="Latest happenings"
                  icon={<FiClock size={17} />}
                  events={data.recent}
                  filterName="recent"
                />
                <EventSection
                  title="My Created Events"
                  subtitle="Events you've organised"
                  icon={<FiEdit2 size={17} />}
                  events={data.my_created_events}
                  filterName="event_creator"
                />
                <EventSection
                  title="Collaborating Events"
                  subtitle="Events you're helping with"
                  icon={<FiUsers size={17} />}
                  events={data.collaborators}
                  filterName="collaborators"
                />
                <EventSection
                  title="Tickets Bought"
                  subtitle="Events you have tickets for"
                  icon={<FiShoppingBag size={17} />}
                  events={data.ticket_bought}
                  filterName="ticket_bought"
                />
                <EventSection
                  title="Draft Events"
                  subtitle="Single events you haven't published yet"
                  icon={<FiFileText size={17} />}
                  events={data.draft_single_events.filter(
                    (e) => e.event_ticketing_type !== 'external',
                  )}
                  filterName="draft"
                  isWarning
                />
                <EventSection
                  title="External Event Drafts"
                  subtitle="External events you haven't published yet"
                  icon={<FiExternalLink size={17} />}
                  events={data.draft_single_events.filter(
                    (e) => e.event_ticketing_type === 'external',
                  )}
                  filterName="draft"
                  isWarning
                />
                <EventSection
                  title="Draft Multi-Events"
                  subtitle="Multi-events you haven't published yet"
                  icon={<FiFileText size={17} />}
                  events={data.draft_multi_events}
                  filterName="multi_event_draft"
                  isWarning
                />
                <EventSection
                  title="Cancelled Events"
                  subtitle="Events that were cancelled"
                  icon={<FiAlertCircle size={17} />}
                  events={data.cancelled}
                  filterName="cancelled"
                  isWarning
                />
              </>
            )}
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
